import {
  buildUfPropertyDirtyPayload,
  normalizePropertyType,
  pickUfPropertyInfo,
} from './ufPropertyFields';
import {
  OPERATIONS,
  OPERATION_LABEL,
  STOCK_STATUSES,
  formatListingPrice,
  getStockStatusLabel,
} from './listingCoreConstants';
import { buildAttributesPayload } from './ufAttributeFormUtils';

export { OPERATIONS, OPERATION_LABEL, STOCK_STATUSES };
export { STOCK_STATUS_ITEMS, STOCK_STATUS_LABELS, getStockStatusLabel } from './listingCoreConstants';

export const NO_OPERATION_VALUE = '__NO_OPERATION__';
export const ALL_OPERATIONS_VALUE = '__ALL__';

/** Fallback legacy: price_type en mayúsculas → operation en minúsculas. */
export function mapOperationFromApi(data) {
  const raw = String(data?.operation ?? '').trim();
  if (raw) {
    const lower = raw.toLowerCase();
    if (OPERATION_LABEL[lower]) return lower;
    const byLabel = OPERATIONS.find((item) => item.label.toLowerCase() === lower);
    if (byLabel) return byLabel.value;
  }
  const legacy = (data?.price_type || '').trim().toUpperCase();
  if (legacy === 'VENTA') return 'venta';
  if (legacy === 'ALQUILER') return 'alquiler';
  if (
    legacy === 'ALQUILER TEMPORARIO'
    || legacy === 'ALQUILER TEMPORAL'
    || legacy === 'O. ALQUILER TEMPORARIO'
  ) {
    return 'alquiler_temporario';
  }
  return raw ? raw.toLowerCase() : null;
}

/** stock_status API → id canónico; desconocido / vacío → available. */
export function mapStockStatusFromApi(data) {
  const raw = data?.stock_status ?? data;
  const id = String(raw ?? '').trim().toLowerCase();
  if (STOCK_STATUSES.includes(id)) return id;
  return 'available';
}

export function resolveUnitStockStatus(unit) {
  return mapStockStatusFromApi(unit);
}

export function emptyUserLotForm() {
  return {
    title: '',
    description: '',
    observations: '',
    unit_label: '',
    covered_surface: '',
    open_surface: '',
    building_type: '',
    rooms: '',
    year_built: '',
    has_water: false,
    has_gas: false,
    has_electricity: false,
    has_sewage: false,
    price: '',
    currency: 'USD',
    operation: '',
    stock_status: 'available',
    is_public: false,
    list_ids: [],
    attributes: {},
    property_type: '',
    property_type_loaded: '',
    property_info: {},
    property_info_loaded: {},
    property_errors: {},
  };
}

/** Normaliza GET /getuserlotdetail (o fila de unidad) → estado del formulario UF. */
export function mapUserLotToForm(data = {}, { observationsDefault } = {}) {
  const propertyInfo = pickUfPropertyInfo(data);
  const incomingListIds = Array.isArray(data.list_ids)
    ? data.list_ids.filter(Boolean)
    : (data.list_id ? [data.list_id] : []);

  return {
    title: data.title || '',
    description: data.description || '',
    observations: observationsDefault !== undefined
      ? observationsDefault
      : (data.observations || ''),
    unit_label: data.unit_label || '',
    covered_surface: data.covered_surface ?? '',
    open_surface: data.open_surface ?? '',
    building_type: data.building_type || '',
    rooms: data.rooms ?? '',
    year_built: data.year_built ?? '',
    has_water: data.has_water || false,
    has_gas: data.has_gas || false,
    has_electricity: data.has_electricity || false,
    has_sewage: data.has_sewage || false,
    price: data.price != null && data.price !== '' ? String(data.price) : '',
    currency: data.currency || 'USD',
    operation: mapOperationFromApi(data) || '',
    stock_status: mapStockStatusFromApi(data),
    is_public: data.is_public || false,
    list_ids: incomingListIds,
    attributes: data.attributes && typeof data.attributes === 'object' ? { ...data.attributes } : {},
    bedrooms: data.bedrooms ?? '',
    full_bathrooms: data.full_bathrooms ?? '',
    parking_lots: data.parking_lots ?? '',
    total_surface: data.total_surface ?? '',
    unit_floor: data.unit_floor ?? '',
    // Mig 090. Anidado: buildUserLotPayload manda solo las claves modificadas
    // (attachUfPropertyFields); en createuserlot null borra.
    property_info: propertyInfo,
    // Snapshot de lo cargado: el payload manda solo lo que cambió (null borra).
    property_info_loaded: propertyInfo,
    property_type: normalizePropertyType(data.property_type || data.property_info?.property_type),
    property_type_loaded: normalizePropertyType(data.property_type || data.property_info?.property_type),
    property_errors: {},
  };
}

/** Payload POST /createuserlot desde el formulario UF. */
function attachOfficialUnitNumber(body, { userLotId, officialUnitNumber }) {
  if (userLotId) {
    body.user_lot_id = userLotId;
    return body;
  }
  // Overlay: `unit_number` tal cual (`"001"`). Nunca en UPDATE (el vínculo no cambia).
  if (officialUnitNumber != null && officialUnitNumber !== '') {
    body.unit_number = String(officialUnitNumber);
  }
  return body;
}

/**
 * Mig 090: `property_type` (si cambió; = building_type) + claves de propiedad modificadas.
 * El subtipo comercial va en `attributes.commercial_subtype` como cualquier atributo.
 */
function attachUfPropertyFields(body, form, schema, { includeFields = true } = {}) {
  if (form.property_type && form.property_type !== form.property_type_loaded) {
    body.property_type = form.property_type;
  }
  if (!includeFields) return body;
  Object.assign(body, buildUfPropertyDirtyPayload(
    form.property_info || {},
    form.property_info_loaded || {},
    schema,
  ));
  return body;
}

export function buildUserLotPayload(form, {
  lotId,
  userLotId,
  schema = null,
  partialStep = null,
  officialUnitNumber = null,
} = {}) {
  if (partialStep === 'building_type') {
    const body = {
      lot_id: lotId,
      building_type: form.building_type || null,
    };
    attachUfPropertyFields(body, form, null, { includeFields: false });
    return attachOfficialUnitNumber(body, { userLotId, officialUnitNumber });
  }

  const body = {
    lot_id: lotId,
    title: form.title?.trim() || null,
    description: form.description?.trim() || null,
    observations: form.observations?.trim() || null,
    unit_label: form.unit_label?.trim() || null,
    price: form.price ? parseFloat(form.price) : null,
    currency: form.currency || null,
    operation: form.operation || null,
    stock_status: mapStockStatusFromApi(form),
    is_public: form.is_public,
    list_ids: Array.isArray(form.list_ids) ? form.list_ids.filter(Boolean) : [],
  };

  attachOfficialUnitNumber(body, { userLotId, officialUnitNumber });

  if (schema && form.building_type) {
    const attributePayload = buildAttributesPayload(form, schema);
    body.building_type = attributePayload.building_type;
    Object.entries(attributePayload).forEach(([key, value]) => {
      if (key !== 'building_type' && key !== 'attributes') {
        body[key] = value;
      }
    });
    if (attributePayload.attributes) body.attributes = attributePayload.attributes;
    return attachUfPropertyFields(body, form, schema);
  }

  // Legacy fallback (sin schema cargado)
  body.covered_surface = form.covered_surface ? parseFloat(form.covered_surface) : null;
  body.open_surface = form.open_surface ? parseFloat(form.open_surface) : null;
  body.building_type = form.building_type || null;
  body.rooms = form.rooms ? parseInt(form.rooms, 10) : null;
  body.year_built = form.year_built ? parseInt(form.year_built, 10) : null;
  body.has_water = form.has_water;
  body.has_gas = form.has_gas;
  body.has_electricity = form.has_electricity;
  body.has_sewage = form.has_sewage;
  if (form.attributes && typeof form.attributes === 'object') {
    body.attributes = form.attributes;
  }
  return attachUfPropertyFields(body, form, null);
}

/** Copy del editor de colección: solo ListingCore de una UF que ya existe. */
export function buildUserLotListingCorePayload(form, { lotId, userLotId } = {}) {
  return {
    lot_id: lotId,
    user_lot_id: userLotId,
    title: form.title?.trim() || null,
    description: form.description?.trim() || null,
    price: form.price ? parseFloat(form.price) : null,
    currency: form.currency || null,
    operation: form.operation || null,
    stock_status: mapStockStatusFromApi(form),
  };
}

export function resolveUnitOperation(unit) {
  return mapOperationFromApi(unit);
}

export function formatUserLotCommercial(unit) {
  const stockLabel = getStockStatusLabel(resolveUnitStockStatus(unit));
  const priceStr = formatListingPrice(unit?.price, unit?.currency);
  const operation = resolveUnitOperation(unit);
  const opLabel = operation ? OPERATION_LABEL[operation] : null;
  return [stockLabel, priceStr, opLabel].filter(Boolean).join(' · ');
}
